export class ApiError extends Error {
  public status: number;
  public code?: string;
  public details?: any;
  public approvalSentTo?: string;
  public deviceId?: string;

  constructor(message: string, status: number, code?: string, details?: any, approvalSentTo?: string, deviceId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.approvalSentTo = approvalSentTo;
    this.deviceId = deviceId;
  }
}

let cachedAccessToken: string | null = typeof window !== 'undefined' ? localStorage.getItem('pb_access_token') : null;

export function setAccessToken(token: string | null) {
  cachedAccessToken = token;
  if (typeof window !== 'undefined') {
    if (token) {
      localStorage.setItem('pb_access_token', token);
    } else {
      localStorage.removeItem('pb_access_token');
    }
  }
}

export function getAccessToken(): string | null {
  if (!cachedAccessToken && typeof window !== 'undefined') {
    cachedAccessToken = localStorage.getItem('pb_access_token');
  }
  return cachedAccessToken;
}

export function getOrCreateBrowserDeviceId(): string {
  let id = typeof window !== 'undefined' ? localStorage.getItem('pb_browser_device_id') : null;
  if (!id && typeof window !== 'undefined') {
    id = 'dev_' + Math.random().toString(36).substring(2, 10) + '_' + Date.now().toString(36);
    localStorage.setItem('pb_browser_device_id', id);
  }
  return id || 'dev_browser_default';
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  if (options.body) {
    headers.set('Content-Type', 'application/json');
  }

  const deviceId = getOrCreateBrowserDeviceId();
  headers.set('x-device-id', deviceId);

  const token = getAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let res = await fetch(endpoint, {
    ...options,
    credentials: 'include',
    headers,
  });

  // Handle Token Expiry & Refresh
  if (res.status === 401 && !endpoint.includes('/api/auth/login') && !endpoint.includes('/api/auth/refresh')) {
    try {
      const refreshRes = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-device-id': deviceId },
      });
      if (refreshRes.ok) {
        const refreshData = (await refreshRes.json()) as { accessToken: string };
        setAccessToken(refreshData.accessToken);
        headers.set('Authorization', `Bearer ${refreshData.accessToken}`);

        // Retry original request
        res = await fetch(endpoint, {
          ...options,
          credentials: 'include',
          headers,
        });
      } else {
        setAccessToken(null);
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.dispatchEvent(new CustomEvent('auth_device_revoked', { detail: { reason: 'session_expired' } }));
        }
      }
    } catch {
      setAccessToken(null);
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.dispatchEvent(new CustomEvent('auth_device_revoked', { detail: { reason: 'session_expired' } }));
      }
    }
  }

  if (!res.ok) {
    let errBody: any;
    try {
      errBody = await res.json();
    } catch {
      errBody = { message: res.statusText };
    }

    // Immediate security lock: If browser access was revoked or deleted by Super Admin, immediately invalidate session
    if (res.status === 403 && errBody?.error === 'DEVICE_UNAPPROVED') {
      setAccessToken(null);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth_device_revoked', { detail: errBody }));
      }
    }

    throw new ApiError(
      errBody.message || 'An error occurred',
      res.status,
      errBody.error,
      errBody.details,
      errBody.approvalSentTo,
      errBody.deviceId
    );
  }

  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return await res.json();
  }
  return (await res.text()) as unknown as T;
}

export const api = {
  // Auth
  login: (body: any) => request<any>('/api/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  verify2Fa: (body: any) => request<any>('/api/auth/2fa/verify', { method: 'POST', body: JSON.stringify(body) }),
  logout: () => request<any>('/api/auth/logout', { method: 'POST' }),
  getMe: () => request<any>('/api/auth/me'),
  getCurrentIp: () => request<{ ip: string }>('/api/auth/current-ip'),
  changePassword: (body: any) => request<any>('/api/auth/change-password', { method: 'POST', body: JSON.stringify(body) }),
  setup2Fa: () => request<any>('/api/auth/2fa/setup', { method: 'POST' }),
  confirm2Fa: (token: string) => request<any>('/api/auth/2fa/confirm', { method: 'POST', body: JSON.stringify({ token }) }),

  // Queue
  getQueue: (shopId?: string) => {
    const url = shopId ? `/api/queue?shopId=${shopId}` : '/api/queue';
    return request<any[]>(url);
  },
  updateOrderOptions: (orderId: string, options: any, shopId?: string) => {
    const url = shopId ? `/api/queue/orders/${orderId}/options?shopId=${shopId}` : `/api/queue/orders/${orderId}/options`;
    return request<any>(url, { method: 'PATCH', body: JSON.stringify(options) });
  },
  turnNextOrder: (orderId: string, shopId?: string, notifyCustomer: boolean = false) => {
    const url = shopId ? `/api/queue/orders/${orderId}/turn-next?shopId=${shopId}` : `/api/queue/orders/${orderId}/turn-next`;
    return request<any>(url, { method: 'POST', body: JSON.stringify({ notifyCustomer }) });
  },
  getArchivedQueue: (shopId?: string) => {
    const url = shopId ? `/api/queue/archived?shopId=${shopId}` : '/api/queue/archived';
    return request<any[]>(url);
  },
  unarchiveOrder: (orderId: string, shopId?: string) => {
    const url = shopId ? `/api/queue/orders/${orderId}/unarchive?shopId=${shopId}` : `/api/queue/orders/${orderId}/unarchive`;
    return request<any>(url, { method: 'POST' });
  },
  updateFileOptions: (fileId: string, options: any, shopId?: string) => {
    const url = shopId ? `/api/queue/files/${fileId}/options?shopId=${shopId}` : `/api/queue/files/${fileId}/options`;
    return request<any>(url, { method: 'PATCH', body: JSON.stringify(options) });
  },
  uploadFile: (fileId: string, data: { base64Data: string; fileName?: string }, shopId?: string) => {
    const url = shopId ? `/api/queue/files/${fileId}/upload?shopId=${shopId}` : `/api/queue/files/${fileId}/upload`;
    return request<any>(url, { method: 'POST', body: JSON.stringify(data) });
  },
  triggerPrint: (orderId: string, options: any, shopId?: string) => {
    const url = shopId ? `/api/queue/orders/${orderId}/print?shopId=${shopId}` : `/api/queue/orders/${orderId}/print`;
    return request<any>(url, { method: 'POST', body: JSON.stringify(options) });
  },
  resetPrintOrder: (orderId: string, shopId?: string) => {
    const url = shopId ? `/api/queue/orders/${orderId}/reset-print?shopId=${shopId}` : `/api/queue/orders/${orderId}/reset-print`;
    return request<any>(url, { method: 'POST' });
  },
  completeOrderManual: (orderId: string, shopId?: string) => {
    const url = shopId ? `/api/queue/orders/${orderId}/complete?shopId=${shopId}` : `/api/queue/orders/${orderId}/complete`;
    return request<any>(url, { method: 'POST' });
  },
  removeFile: (fileId: string, shopId?: string) => {
    const url = shopId ? `/api/queue/files/${fileId}?shopId=${shopId}` : `/api/queue/files/${fileId}`;
    return request<any>(url, { method: 'DELETE' });
  },
  getDiagnostics: (shopId?: string) => {
    const url = shopId ? `/api/queue/diagnostics?shopId=${shopId}` : '/api/queue/diagnostics';
    return request<any>(url);
  },
  simulateTestOrder: (body?: any, shopId?: string) => {
    const url = shopId ? `/api/queue/simulate-test-order?shopId=${shopId}` : '/api/queue/simulate-test-order';
    return request<any>(url, { method: 'POST', body: JSON.stringify(body || {}) });
  },
  cancelOrder: (orderId: string, shopId?: string) => {
    const url = shopId ? `/api/queue/orders/${orderId}?shopId=${shopId}` : `/api/queue/orders/${orderId}`;
    return request<any>(url, { method: 'DELETE' });
  },
  clearDemoOrders: (shopId?: string) => {
    const url = shopId ? `/api/queue/clear-demo?shopId=${shopId}` : '/api/queue/clear-demo';
    return request<any>(url, { method: 'POST' });
  },
  simulateRevocation: (fileId: string, shopId?: string) => {
    const url = shopId ? `/api/queue/simulate-revocation?shopId=${shopId}` : '/api/queue/simulate-revocation';
    return request<any>(url, { method: 'POST', body: JSON.stringify({ fileId }) });
  },
  testExpiration: (shopId?: string) => {
    const url = shopId ? `/api/queue/test-expiration?shopId=${shopId}` : '/api/queue/test-expiration';
    return request<any>(url, { method: 'POST' });
  },

  // Shop Settings, Switches & Analytics
  getShopSettings: (shopId?: string) => {
    const url = shopId ? `/api/shop/settings?shopId=${shopId}` : '/api/shop/settings';
    return request<any>(url);
  },
  updateMessages: (messages: any, shopId?: string) => {
    const url = shopId ? `/api/shop/settings/messages?shopId=${shopId}` : '/api/shop/settings/messages';
    return request<any>(url, { method: 'PATCH', body: JSON.stringify(messages) });
  },
  updateShopSwitches: (switches: any, shopId?: string) => {
    const url = shopId ? `/api/shop/settings/switches?shopId=${shopId}` : '/api/shop/settings/switches';
    return request<any>(url, { method: 'PATCH', body: JSON.stringify(switches) });
  },
  updateSystemConfig: (system: any, shopId?: string) => {
    const url = shopId ? `/api/shop/settings/system?shopId=${shopId}` : '/api/shop/settings/system';
    return request<any>(url, { method: 'PATCH', body: JSON.stringify(system) });
  },
  updateInventory: (inventory: any, shopId?: string) => {
    const url = shopId ? `/api/shop/settings/inventory?shopId=${shopId}` : '/api/shop/settings/inventory';
    return request<any>(url, { method: 'PATCH', body: JSON.stringify(inventory) });
  },
  addReam: (count: number = 1, shopId?: string) => {
    const url = shopId ? `/api/shop/inventory/add-ream?shopId=${shopId}` : '/api/shop/inventory/add-ream';
    return request<any>(url, { method: 'POST', body: JSON.stringify({ count }) });
  },
  getAnalytics: (shopId?: string) => {
    const url = shopId ? `/api/shop/analytics?shopId=${shopId}` : '/api/shop/analytics';
    return request<any>(url);
  },

  // Admin Management
  getShops: () => request<any[]>('/api/admin/shops'),
  getShop: (id: string) => request<any>(`/api/admin/shops/${id}`),
  createShop: (body: any) => request<any>('/api/admin/shops', { method: 'POST', body: JSON.stringify(body) }),
  updateShop: (id: string, body: any) => request<any>(`/api/admin/shops/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteShop: (id: string, confirmName: string) => request<any>(`/api/admin/shops/${id}`, { method: 'DELETE', body: JSON.stringify({ confirmName }) }),
  extendSubscription: (shopId: string, options: { months?: number; days?: number } | number) => {
    const body = typeof options === 'number' ? { months: options } : options;
    return request<any>(`/api/admin/shops/${shopId}/subscription/extend`, { method: 'POST', body: JSON.stringify(body) });
  },
  setManualSubscription: (shopId: string, validUntil: string, graceDays = 3) => {
    return request<any>(`/api/admin/shops/${shopId}/subscription/manual`, {
      method: 'POST',
      body: JSON.stringify({ validUntil, graceDays }),
    });
  },
  getSubscriptionHistory: (shopId: string) => {
    return request<any[]>(`/api/admin/shops/${shopId}/subscription/history`);
  },
  toggleSuspendShop: (shopId: string) => request<any>(`/api/admin/shops/${shopId}/subscription/toggle-suspend`, { method: 'POST' }),
  saveWhatsAppCredentials: (shopId: string, creds: any) => request<any>(`/api/admin/shops/${shopId}/whatsapp`, { method: 'POST', body: JSON.stringify(creds) }),
  getWhatsAppCredentials: (shopId: string) => request<any>(`/api/admin/shops/${shopId}/whatsapp`),
  testWhatsAppCredentials: (shopId: string, creds?: any) => request<any>(`/api/admin/shops/${shopId}/whatsapp/test`, { method: 'POST', body: JSON.stringify(creds || {}) }),
  getWhatsAppStatus: (shopId: string) => request<any>(`/api/admin/shops/${shopId}/whatsapp/status`),
  startBaileys: (shopId: string, force?: boolean) => request<any>(`/api/admin/shops/${shopId}/whatsapp/baileys-start${force ? '?force=true' : ''}`, { method: 'POST' }),
  getBaileysStatus: (shopId: string) => request<any>(`/api/admin/shops/${shopId}/whatsapp/baileys-status`),
  disconnectBaileys: (shopId: string) => request<any>(`/api/admin/shops/${shopId}/whatsapp/baileys-disconnect`, { method: 'POST' }),
  startShopBaileys: (shopId?: string, force?: boolean) => {
    let url = shopId ? `/api/shop/whatsapp/baileys-start?shopId=${shopId}` : '/api/shop/whatsapp/baileys-start';
    if (force) {
      url += (url.includes('?') ? '&' : '?') + 'force=true';
    }
    return request<any>(url, { method: 'POST' });
  },
  getShopBaileysStatus: (shopId?: string) => {
    const url = shopId ? `/api/shop/whatsapp/baileys-status?shopId=${shopId}` : '/api/shop/whatsapp/baileys-status';
    return request<any>(url);
  },
  disconnectShopBaileys: (shopId?: string) => {
    const url = shopId ? `/api/shop/whatsapp/baileys-disconnect?shopId=${shopId}` : '/api/shop/whatsapp/baileys-disconnect';
    return request<any>(url, { method: 'POST' });
  },
  testShopWhatsAppConnection: (shopId?: string) => {
    const url = shopId ? `/api/queue/test-whatsapp-connection?shopId=${shopId}` : '/api/queue/test-whatsapp-connection';
    return request<any>(url, { method: 'POST' });
  },
  regenerateAgentKey: (shopId: string) => request<any>(`/api/admin/shops/${shopId}/agent/regenerate-key`, { method: 'POST' }),
  getShopkeeperDevices: (shopId?: string) => request<any[]>(`/api/shop/devices${shopId ? `?shopId=${shopId}` : ''}`),
  toggleShopkeeperDeviceApproval: (deviceId: string, approved: boolean, shopId?: string) =>
    request<any>(`/api/shop/devices/${deviceId}/approval${shopId ? `?shopId=${shopId}` : ''}`, { method: 'PATCH', body: JSON.stringify({ approved }) }),
  deleteShopkeeperDevice: (deviceId: string, shopId?: string) =>
    request<any>(`/api/shop/devices/${deviceId}${shopId ? `?shopId=${shopId}` : ''}`, { method: 'DELETE' }),

  getShopDevices: (shopId: string) => request<any[]>(`/api/admin/shops/${shopId}/devices`),
  getAllDevices: () => request<any[]>('/api/admin/devices'),
  getSuperDevices: () => request<any[]>('/api/admin/super/devices'),
  getShopPairing: (shopId: string) => request<any>(`/api/admin/shops/${shopId}/pairing`),
  toggleDeviceApproval: (deviceId: string, approved: boolean) => request<any>(`/api/admin/devices/${deviceId}/approval`, { method: 'PATCH', body: JSON.stringify({ approved }) }),
  deleteDevice: (deviceId: string) => request<any>(`/api/admin/devices/${deviceId}`, { method: 'DELETE' }),

  getShopkeeperCredentials: (shopId: string) => request<any>(`/api/admin/shops/${shopId}/shopkeeper`),
  updateShopkeeperCredentials: (shopId: string, body: { username?: string; email?: string; password?: string }) =>
    request<any>(`/api/admin/shops/${shopId}/shopkeeper`, { method: 'PATCH', body: JSON.stringify(body) }),
  updateAdminCredentials: (adminId: string, body: { username?: string; email?: string; password?: string }) =>
    request<any>(`/api/admin/admins/${adminId}/credentials`, { method: 'PATCH', body: JSON.stringify(body) }),

  // Super Admin
  getAdmins: () => request<any[]>('/api/admin/admins'),
  createAdmin: (body: any) => request<any>('/api/admin/admins', { method: 'POST', body: JSON.stringify(body) }),
  toggleAdminStatus: (id: string, status: 'ACTIVE' | 'DISABLED') => request<any>(`/api/admin/admins/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  deleteAdmin: (id: string) => request<any>(`/api/admin/admins/${id}`, { method: 'DELETE' }),
  getPlatformConfig: () => request<any>('/api/admin/platform-config'),
  updatePlatformConfig: (config: any) => request<any>('/api/admin/platform-config', { method: 'PUT', body: JSON.stringify(config) }),
  getAuditLogs: () => request<any[]>('/api/admin/audit-logs'),
  approveDevicePin: (deviceId: string, pin: string) =>
    request<any>('/api/auth/approve-device-pin', { method: 'POST', body: JSON.stringify({ deviceId, pin }) }),

  // Customer Subscriptions (Shop)
  getShopSubscriptions: (shopId?: string) => {
    const url = shopId ? `/api/shop/subscriptions?shopId=${shopId}` : '/api/shop/subscriptions';
    return request<any[]>(url);
  },
  createShopSubscription: (body: { phone: string; label?: string; days?: number; validUntil?: string }, shopId?: string) => {
    const url = shopId ? `/api/shop/subscriptions?shopId=${shopId}` : '/api/shop/subscriptions';
    return request<any>(url, { method: 'POST', body: JSON.stringify(body) });
  },
  updateShopSubscription: (id: string, body: { phone?: string; label?: string; addDays?: number; validUntil?: string }, shopId?: string) => {
    const url = shopId ? `/api/shop/subscriptions/${id}?shopId=${shopId}` : `/api/shop/subscriptions/${id}`;
    return request<any>(url, { method: 'PUT', body: JSON.stringify(body) });
  },
  deleteShopSubscription: (id: string, shopId?: string) => {
    const url = shopId ? `/api/shop/subscriptions/${id}?shopId=${shopId}` : `/api/shop/subscriptions/${id}`;
    return request<any>(url, { method: 'DELETE' });
  },

  // Global Customer Subscriptions (Super Admin)
  getGlobalSubscriptions: () => request<any[]>('/api/admin/subscriptions'),
  createGlobalSubscription: (body: { phone: string; label?: string; days?: number; validUntil?: string }) =>
    request<any>('/api/admin/subscriptions', { method: 'POST', body: JSON.stringify(body) }),
  updateGlobalSubscription: (id: string, body: { phone?: string; label?: string; addDays?: number; validUntil?: string }) =>
    request<any>(`/api/admin/subscriptions/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteGlobalSubscription: (id: string) => request<any>(`/api/admin/subscriptions/${id}`, { method: 'DELETE' }),

  downloadAgentZip: async (shopId: string, shopName?: string): Promise<void> => {
    const token = getAccessToken();
    const deviceId = getOrCreateBrowserDeviceId();
    const headers = new Headers();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    headers.set('x-device-id', deviceId);

    const queryParams = new URLSearchParams({
      shopId,
      token: token || '',
      deviceId: deviceId || '',
    });

    const res = await fetch(`/api/shop/agent/download?${queryParams.toString()}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!res.ok) {
      let errMsg = `Download failed (${res.status})`;
      try {
        const errJson = await res.json();
        errMsg = errJson.message || errJson.error || errMsg;
      } catch {
        // ignore
      }
      throw new Error(errMsg);
    }

    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition');
    let filename = `PrintBridge_Agent_${(shopName || 'Shop').replace(/[^a-zA-Z0-9_-]/g, '_')}.zip`;
    if (disposition && disposition.includes('filename="')) {
      filename = disposition.split('filename="')[1].split('"')[0];
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
};

